// https://codesandbox.io/p/sandbox/plotly-js-heatmap-7fmizo?file=%2Fsrc%2FApp.js

import numeral from "numeral";
import React, { useEffect, useMemo, useState } from "react";

import { getDistributionData } from "../api/data";
import { ExploreSchema, ExploreVector } from "../core/global";
import { getExploreColumnKeys } from "../helpers/core";
import { DistributionTable } from "./distribution-table";
import { ErrorMessage, WarningMessage } from "./error-message";
import { ExploreSelect } from "./explore-select";
import Loader from "./loader";

interface ExecProps {
  preview: boolean;
  filename: string;
  provider: string | null;
  model: string | null;
  geography: string | null;
  version: string | null;
}

interface ExploreDistributionPlotProps {
  exploreDataSchema: ExploreSchema;
  loading: boolean;
}
export const ExploreDistributionPlot = ({
  exploreDataSchema,
  loading,
}: ExploreDistributionPlotProps) => {
  const [error, setError] = useState<string[] | null>(null)
  const [warning, setWarning] = useState<string[] | null>(null)
  const [dataLoading, setDataLoading] = useState<boolean>(false)
  const [plotData, setPlotData] = useState<number[][]>([])
  const [xAxis, setXAxis] = useState<(string | number)[]>([]);
  const [yAxis, setYAxis] = useState<(string | number)[]>([]);
  const [columnTotals, setColumnTotals] = useState<(string | number)[]>([]);
  const [rowsTotals, setRowsTotals] = useState<(string | number)[]>([]);
  const [xAxisEdges, setXAxisEdges] = useState<boolean | null>(null);
  const [yAxisEdges, setYAxisEdges] = useState<boolean | null>(null);
  const [xAxisDirectional, setXAxisDirectional] = useState<boolean | null>(null);
  const [yAxisDirectional, setYAxisDirectional] = useState<boolean | null>(null);

  const [keyOne, setKeyOne] = useState<string | null>(null);
  const [keyTwo, setKeyTwo] = useState<string | null>(null);
  const [keyThree, setKeyThree] = useState<string | null>(null);
  const [keyFour, setKeyFour] = useState<string | null>(null);

  useEffect(() => {
    setError(null)
    setWarning(null)

    const execute = async ({
      preview,
      filename,
      provider,
      model,
      geography,
      version
    }: ExecProps) => {
      setDataLoading(true)
      const result = await getDistributionData({
        vector_one: keyOne,
        vector_two: keyTwo,
        granularity: 12,
        time: keyThree || 'All',
        preview,
        filename,
        orderId: exploreDataSchema?.description?.orderId || null,
        provider,
        model,
        geography,
        version: version === 'null' ? null : version
      })
      const {error, response} = result

      
      if (error === 200 && response) {
        const {values, xAxis, xAxisEdges, xDirectional, yAxis, yAxisEdges, yDirectional, columnTotals, rowTotals} = response
        if (values.length === 0) {
          setWarning([`Selected vector **(${keyOne})** and **(${keyTwo})** cannot be rendered for given location.`])
        } else {
          setPlotData(values || []);
          setXAxis((xAxis || []));
          setYAxis((yAxis || []));
          setColumnTotals((columnTotals || []));
          setRowsTotals((rowTotals || []));
          setXAxisEdges(xAxisEdges || false);
          setYAxisEdges(yAxisEdges || false);
          setXAxisDirectional(xDirectional || false);
          setYAxisDirectional(yDirectional || false);
        }
      } else {
        setError([`Problem loading distribution data for **${keyOne}**, **${keyTwo}**`])
      }
      setDataLoading(false)
    }
    if (exploreDataSchema) {

      const {
        description: { 
          model = null, 
          provider = null, 
          geography = null, 
          version = null, 
          filename, 
          uid,
        },
      } = exploreDataSchema;
      // const [provider = '', model = '', geography = '', version = null] = modelKey.split('-')
      
      const preview = !!(uid && `${uid}`.indexOf('preview') !== -1)
      if (keyOne && keyTwo) {
        execute({
          preview, 
          filename,
          provider,
          model,
          geography,
          version
        })
      }
    }
  }, [keyOne, keyTwo, keyThree, exploreDataSchema]);

  const [latStr, lonStr, model, variableOptions, titleOne, titleTwo] = useMemo(() => {
    let titleOne = ''
    let titleTwo = ''
    let latStr = ''
    let lonStr = ''
    const model = ''
    let variableOptions: any[] = []
    
    if (exploreDataSchema) {
      const {
        vectors,
        description: { model, lat, lon },
      } = exploreDataSchema;

      latStr = numeral(lat).format("0.00000");
      lonStr = numeral(lon).format("0.00000");
      
      // const option = vectors.find((v: ExploreVector) => v.key === keyOne);
      // const units = option?.units || "";

      variableOptions = getExploreColumnKeys(
        exploreDataSchema,
        'all'
      )

      let found = vectors.find((v: any) => v.key === keyOne)
      if (found) {
        titleOne = found.name
      }
      found = vectors.find((v: any) => v.key === keyTwo)
      if (found) {
        titleTwo = found.name
      }

    }
    return [latStr, lonStr, model, variableOptions, titleOne, titleTwo];
  }, [exploreDataSchema, keyOne, keyTwo]);

  const isLoading =
    keyOne === null
    || keyTwo === null
    || exploreDataSchema === null
    || loading
    || dataLoading;

  return (
    <>
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "flex-start", "width": "100%" }}>
        <ExploreSelect
          title="Variable One"
          columnKeys={variableOptions}
          onChangeKey={(key) => {
            if (key && Array.isArray(key)) {
              setKeyOne(key[0]);
            } else {
              setKeyOne(null);
            }
          }}
        />
        <ExploreSelect
          title="Variable Two"
          columnKeys={variableOptions}
          initialValue={variableOptions.length > 1 ? 1 : 0}
          onChangeKey={(key)=>{
            if (key && Array.isArray(key)) {
              setKeyTwo(key[0]);
            } else {
              setKeyTwo(null);
            }
          }} 
        />
        <ExploreSelect
          title="Time"
          columnKeys={[
            "All",
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ]}
          onChangeKey={(key)=>{
            if (key && Array.isArray(key)) {
              setKeyThree(key[0]);
            } else {
              setKeyThree(null);
            }
          }} 
        />
        {/* <ExploreSelect
          title="No. of Columns"
          columnKeys={["4", "8", "12", "16", "32", "36"]}
          initialValue={2}
          onChangeKey={(key)=>{
            if (key && Array.isArray(key)) {
              setKeyFour(key[0]);
            } else {
              setKeyFour(null);
            }
          }} 
        /> */}
      </div>
      {isLoading ? (
        <div style={{ width: "100%", padding: "240px 0 64px 0" }}>
          <Loader center showCog />
        </div>
      ) : (
        <>
        {warning && <WarningMessage message={warning} />}
        {error && <ErrorMessage message={error} />}
        {(error === null && warning === null) && 
          <div
            style={{
              height: "550px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <DistributionTable
              plotData={[...plotData].reverse()}
              xAxis={xAxis}
              yAxis={[...yAxis].reverse()}
              columnTotals={[...columnTotals]}
              rowsTotals={[...rowsTotals.reverse()]}
              title={`${keyOne} vs ${keyTwo} Distribution`}
              xAxisTitle={`${titleTwo} [${exploreDataSchema?.vectors?.find((v: ExploreVector) => v.key === keyTwo)?.units || ''}]`}
              yAxisTitle={`${titleOne} [${exploreDataSchema?.vectors?.find((v: ExploreVector) => v.key === keyOne)?.units || ''}]`}
              min={0}
              max={Math.max(...plotData.flat())}
              xAxisEdges={xAxisEdges}
              yAxisEdges={yAxisEdges}
              xAxisDirectional={xAxisDirectional}
              yAxisDirectional={yAxisDirectional}
            />
          </div>
        }
      </>
    )}
  </>
  );
};
